import { CommonModule, NgComponentOutlet } from '@angular/common';
import { Component, Inject, OnDestroy, ViewChild } from '@angular/core';
import { AbstractControl, FormArray, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { filter, finalize, Subscription } from 'rxjs';
import { IconModule } from 'src/app/icon/icon.module';
import { MaterialModule } from 'src/app/material.module';
import { IFormDialogConfig, IFormDialogContent } from 'src/app/shared/interfaces/IFormDialog';

@Component({
  selector: 'app-form-dialog',
  imports: [CommonModule, NgComponentOutlet, IconModule, MaterialModule, TranslateModule],
  templateUrl: './form-dialog.component.html',
  styleUrl: './form-dialog.component.scss',
  host: { class: 'block h-full' }
})
export class FormDialogComponent implements OnDestroy {
  @ViewChild(NgComponentOutlet) private formOutlet?: NgComponentOutlet;

  isSubmitting = false;
  showDiscardConfirmation = false;
  unsubscribe: Subscription[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public readonly config: IFormDialogConfig,
    private readonly dialogRef: MatDialogRef<FormDialogComponent>
  ) {
    this.unsubscribe.push(
      this.dialogRef.backdropClick().subscribe(() => this.requestClose()),
      this.dialogRef.keydownEvents().pipe(filter((event) => event.key === 'Escape')).subscribe(() => this.requestClose())
    );
  }

  get formContent(): IFormDialogContent | null {
    return (this.formOutlet?.componentInstance as IFormDialogContent | null) ?? null;
  }

  requestClose(): void {
    if (this.isSubmitting) return;

    const shouldConfirm = this.config.confirmDiscardChanges !== false && this.formContent?.form?.dirty;
    if (shouldConfirm) {
      this.showDiscardConfirmation = true;
      return;
    }

    this.dialogRef.close();
  }

  keepEditing(): void {
    this.showDiscardConfirmation = false;
  }

  discardChanges(): void {
    this.dialogRef.close();
  }

  submit(): void {
    const content = this.formContent;
    if (!content || this.isSubmitting) return;

    this.normalizeTextValues(content.form);
    if (content.form.invalid) {
      content.form.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;
    content.submit().pipe(finalize(() => (this.isSubmitting = false))).subscribe({
      next: (result) => this.dialogRef.close(result),
      error: (error) => content.handleSubmitError?.(error)
    });
  }

  private normalizeTextValues(control: AbstractControl): void {
    if (control instanceof FormGroup || control instanceof FormArray) {
      Object.values(control.controls).forEach((childControl) => this.normalizeTextValues(childControl));
      return;
    }

    if (typeof control.value !== 'string') return;

    const trimmedValue = control.value.trim();
    control.setValue(trimmedValue || null);
  }

  ngOnDestroy(): void {
    this.unsubscribe.forEach((subscription) => subscription.unsubscribe());
  }
}
